// La carte de publication.
//
// C'est la pièce la plus vue de Koinonia : elle apparaît dans le fil, sur un
// profil, dans un groupe, sur une page, dans la recherche. Elle a donc été
// gardée pour la fin de la migration, et traduite d'un bloc plutôt qu'à moitié.
//
// Une chose qu'elle faisait à la main et qui disparaît ici : l'ancienne version
// mettait à jour les cartes existantes au lieu de tout redessiner, pour ne pas
// refermer les commentaires ouverts ni perdre le focus du champ de saisie. Il
// fallait pour cela tenir une carte `postId -> élément`, replacer chaque nœud au
// bon endroit, et se souvenir de ne PAS reconstruire. React fait exactement cela
// tout seul, à partir de la clé.

import { useEffect, useRef, useState } from "react";
import Store from "../store.js";
import { APP } from "../config.js";
import { ilYA, toast, modale, fermerModale, confirmer, chargement, esc, avatar, apercuImage } from "../ui.js";
import { expliquer } from "../algorithmes.js";
import { partager, signaler } from "../views/partage.js";
import Avatar from "./Avatar.jsx";
import Icone, { IconeDuo } from "./Icone.jsx";
import Certifie from "./Certifie.jsx";
import TexteRiche from "./TexteRiche.jsx";
import QuiAReagi from "./QuiAReagi.jsx";
import EchoDuCercle from "./EchoDuCercle.jsx";
import Commentaires from "./Commentaires.jsx";
import { useAmens, useStatuts, useMembre, useCertifiePage } from "./hooks.js";
import { reaction } from "../reactions.js";
import { Palette, Pastille, Resume, resumerCompteurs, usePalette } from "./Reactions.jsx";

const TYPES = {
  ...Object.fromEntries(APP.typesPost.map((t) => [t.id, t])),
  partage: { id: "partage", icone: "🔁", label: "Partage" },
};

// Publications fabriquées par l'application, pas écrites par le membre.
const GESTES = {
  photo_profil: "a changé sa photo de profil",
  photo_couverture: "a changé sa photo de couverture",
};

/* Ce que dit le bouton. Il dit « Amen » tant qu'on n'a rien posé, et le nom de
   SA réaction dès qu'on en a posé une — comme le « J'aime » de Facebook devient
   « J'adore ». Sur une demande de prière, l'Amen s'appelle « Je prie » : c'est
   le même geste, et ce n'est pas le même mot. */
const libelleAmen = (type, mienne) => {
  if (mienne && mienne !== "amen") return reaction(mienne).label;
  return type === "priere" ? "Je prie" : "Amen";
};

// En français, zéro et un prennent le singulier : « 0 personne prie », et non
// « 0 personnes prient » comme l'écrivait l'ancienne version.
const compteurAmen = (p, total) => {
  const n = total ?? p.amens ?? 0;
  return p.type === "priere"
    ? `${n} ${n > 1 ? "personnes prient" : "personne prie"}`
    : `${n} amen${n > 1 ? "s" : ""}`;
};

const pluriel = (n, mot) => `${n} ${mot}${n > 1 ? "s" : ""}`;

/* ------------------------------------------------------------------- média */

/**
 * Les photos d'une publication.
 *
 * Une seule : elle s'affiche en grand, comme avant. Plusieurs : une grille —
 * deux côte à côte, trois avec une grande à gauche, quatre en carré, et au-delà
 * un « +N » sur la dernière, comme sur Facebook. Toucher une photo l'ouvre en
 * plein écran.
 *
 * `images` est le champ des albums ; `image` reste celui d'avant et porte la
 * première. Rien de ce qui a été publié jusqu'ici n'a besoin d'être réécrit.
 */
function Media({ p, actions }) {
  if (p.video) return <video className="post__video" src={p.video} controls playsInline preload="metadata" />;

  const photos = p.images?.length ? p.images : p.image ? [p.image] : [];
  if (!photos.length) return null;

  if (photos.length === 1)
    return (
      <img
        className="post__image"
        src={photos[0]}
        alt=""
        loading="lazy"
        onClick={() => apercuImage(photos[0], "", actions)}
      />
    );

  // Au-delà de quatre, on n'en montre que quatre : la cinquième vignette serait
  // trop petite pour qu'on y distingue quoi que ce soit.
  const visibles = photos.slice(0, 4);
  const reste = photos.length - visibles.length;

  return (
    <div className={`album album--${visibles.length}`}>
      {visibles.map((u, i) => (
        <button
          type="button"
          className="album__case"
          key={u}
          aria-label={`Photo ${i + 1} sur ${photos.length}`}
          onClick={() => apercuImage(u, "", actions)}
        >
          <img src={u} alt="" loading="lazy" />
          {i === visibles.length - 1 && reste > 0 && <span className="album__reste">+{reste}</span>}
        </button>
      ))}
    </div>
  );
}

/** La publication d'origine, encadrée, quand il s'agit d'un partage. */
function Citation({ o, ctx }) {
  const auteur = useMembre(ctx, o.authorId, { nom: o.auteurNom, photo: o.auteurPhoto });
  return (
    <blockquote className="repartage">
      <a className="repartage__auteur" href={`#/profil/${o.authorId}`}>
        <Avatar membre={auteur} taille={30} />
        <strong>
          {auteur.nom}
          <Certifie membre={auteur} taille={12} />
        </strong>
        <span className="petit texte-doux">{ilYA(o.createdAt)}</span>
      </a>
      {o.texte && (
        <div className="repartage__texte">
          <TexteRiche texte={o.texte} mentions={o.mentions} />
        </div>
      )}
      <Media p={o} />
    </blockquote>
  );
}

/* ------------------------------------------------------------------ fenêtres */

/**
 * L'écran doit pouvoir répondre au membre qui demande pourquoi une publication
 * lui est montrée. Un classement qu'on ne sait pas expliquer est un classement
 * qu'on ne sait pas corriger.
 */
function pourquoi(raisons) {
  const textes = expliquer(raisons, 4);
  modale({
    titre: "Pourquoi je vois ça ?",
    corps: `
      <p class="petit texte-doux">Koinonia classe votre fil selon vos liens réels, jamais selon ce qui fait le plus réagir.</p>
      <ul class="politique-regles">
        ${textes.length ? textes.map((t) => `<li>${esc(t)}</li>`).join("") : "<li>Cette publication vous est montrée parce qu'elle est récente.</li>"}
      </ul>
      <p class="petit texte-doux">Le détail des règles se trouve dans « Règles de la communauté », au Menu.</p>`,
  });
}

/** Qui a vu cette publication. Réservé à son auteur. */
async function montrerVues(p) {
  const { el: fond } = modale({ titre: "Qui a vu cette publication", corps: chargement("Un instant…") });
  const corps = () => fond.querySelector(".modal__corps") || fond;
  try {
    const gens = await Store.vues.liste("posts", p.id);
    corps().innerHTML = gens.length
      ? `<div class="grille-membres">${gens
          .map(
            (v) => `
        <a class="membre" href="#/profil/${esc(v.uid)}">
          <span class="membre__qui">
            ${avatar({ nom: v.nom, photo: v.photo }, 44)}
            <span><strong>${esc(v.nom || "Membre")}</strong>
            <span class="petit texte-doux">${esc(ilYA(v.le))}</span></span>
          </span>
        </a>`
          )
          .join("")}</div>`
      : `<p class="texte-doux petit">Personne ne l'a encore ouverte.</p>`;
  } catch (e) {
    corps().innerHTML = `<p class="texte-doux petit">${esc(e.message || "Liste indisponible")}</p>`;
  }
}

function menu(p, ctx, raisons) {
  const estMoi = p.authorId === ctx.moi.uid;
  modale({
    titre: "Options",
    corps: `<div class="menu-liste">
      ${estMoi ? `<button class="menu-item" data-x="modif">✏️ Modifier la publication</button>` : ""}
      ${estMoi ? `<button class="menu-item" data-x="suppr">🗑️ Supprimer la publication</button>` : ""}
      ${!estMoi ? `<button class="menu-item" data-x="msg">✉️ Envoyer un message privé</button>` : ""}
      <button class="menu-item" data-x="copier">🔗 Copier le texte</button>
      ${raisons ? `<button class="menu-item" data-x="pourquoi">🔍 Pourquoi je vois ça ?</button>` : ""}
      ${!estMoi ? `<button class="menu-item" data-x="bloquer">🚫 Bloquer ${esc(p.auteurNom || "ce membre")}</button>` : ""}
      ${!estMoi ? `<button class="menu-item menu-item--danger" data-x="signal">🚩 Signaler</button>` : ""}
    </div>`,
  }).el.addEventListener("click", async (e) => {
    const b = e.target.closest("[data-x]");
    if (!b) return;
    fermerModale();
    const quoi = b.dataset.x;
    if (quoi === "modif") {
      /* ⚠️ CORRIGER SA PUBLICATION — 24 août 2026, demandé par Josaphat.
         Le serveur l'autorisait depuis toujours (`allow update: if …
         resource.data.authorId == moi()`), mais aucune fonction ne l'appelait :
         une faute de frappe était définitive, et il fallait effacer sa
         publication pour la réécrire — en y perdant ses réactions et ses
         commentaires.

         ⚠️ LE TEXTE SEUL. La façade du cerveau n'accepte qu'une CHAÎNE, pas un
         objet : aucun écran ne peut donc glisser un `authorId` ou une date dans
         une correction. Ce qu'on ne peut pas exprimer ne peut pas être
         détourné.

         ⚠️ ET CELA LAISSE UNE TRACE — `modifieLe`, affiché « modifié » sous le
         nom. Réécrire en silence ce que dix personnes ont approuvé, ce serait
         déplacer une signature sous les yeux de ceux qui ont signé. */
      const { el } = modale({
        titre: "Modifier la publication",
        corps: `
          <textarea class="champ" id="modif-texte" rows="6"
                    maxlength="3000">${esc(p.texte || "")}</textarea>
          <p class="petit texte-doux">Seul le texte change. Les photos, les
          réactions et les commentaires restent.</p>`,
        actions: [
          { label: "Annuler" },
          {
            label: "Enregistrer",
            classe: "btn--primaire",
            onClick: async (fond) => {
              const t = fond.querySelector("#modif-texte").value;
              const bouton = fond.querySelector('[data-action="1"]');
              bouton.disabled = true;
              bouton.textContent = "Enregistrement…";
              try {
                await Store.posts.modifier(p.id, t);
                fermerModale();
                toast("Publication modifiée");
              } catch (err) {
                bouton.disabled = false;
                bouton.textContent = "Enregistrer";
                /* La façade du cerveau traduit déjà les refus du serveur
                   avant de les lancer — voir `traduireRefus` dans store.js.
                   Le message est donc déjà en français, et il nomme le geste. */
                toast(err?.message || "Correction impossible", "erreur");
              }
            },
          },
        ],
      });
      el.querySelector("#modif-texte").focus();
    } else if (quoi === "suppr") {
      if (await confirmer("Supprimer ?", "Cette publication et ses commentaires seront définitivement supprimés.", "Supprimer")) {
        await Store.posts.remove(p.id);
        toast("Publication supprimée");
      }
    } else if (quoi === "msg") {
      location.hash = `#/messages/${await Store.convos.ensure(p.authorId)}`;
    } else if (quoi === "pourquoi") {
      pourquoi(raisons);
    } else if (quoi === "copier") {
      navigator.clipboard?.writeText(p.texte).then(() => toast("Texte copié"));
    } else if (quoi === "bloquer") {
      const { bloquerMembre } = await import("../views/blocages.js");
      bloquerMembre({ uid: p.authorId, nom: p.auteurNom });
    } else if (quoi === "signal") {
      signaler({ cibleType: "publication", cibleId: p.id, auteurId: p.authorId, apercu: (p.texte || "").slice(0, 200) });
    }
  });
}

/* -------------------------------------------------------------------- carte */

export default function Publication({ p, ctx, raisons, observateurVus, commentairesDesLeDepart = false }) {
  // Ouverts d'emblée sur la page d'une seule publication : on n'y arrive jamais
  // par hasard, et ce qui se dit dessous fait partie de ce qu'on vient voir.
  const [commentairesOuverts, setCommentairesOuverts] = useState(commentairesDesLeDepart);
  const amens = useAmens(ctx);
  const avecStatut = useStatuts(ctx);
  // Demandés à l'instant, jamais recopiés : un compte certifié aujourd'hui doit
  // porter sa coche sur ce qu'il a écrit hier, et un membre qui ajoute enfin sa
  // photo doit apparaître sur toutes ses publications, pas seulement les
  // prochaines. Le nom et la photo figés dans la publication ne servent plus
  // que de secours, le temps que le profil réponde.
  const auteur = useMembre(ctx, p.pageId ? null : p.authorId, {
    nom: p.auteurNom,
    photo: p.auteurPhoto,
  });
  const pageCertifiee = useCertifiePage(p.pageId);
  const carte = useRef(null);
  const soutenu = amens.has(p.id);
  const mienne = amens.get(p.id) || null;
  const palette = usePalette();
  const resume = resumerCompteurs(p, mienne);
  const [quiReagit, setQuiReagit] = useState(false);

  // Une publication réellement passée sous les yeux du membre est notée comme
  // vue : le classement cesse alors de la remettre en tête.
  useEffect(() => {
    const el = carte.current;
    const obs = observateurVus?.current;
    if (!el || !obs) return;
    obs.observe(el);
    return () => obs.unobserve(el);
  }, [observateurVus]);

  /**
   * Poser une réaction.
   *
   * `cle` à `null` la retire ; reposer la même la retire aussi — c'est le
   * pilote qui en décide, pour que les trois écrans se comportent pareil.
   *
   * L'auteur n'est prévenu que d'un AJOUT, jamais d'un retrait ni d'un
   * changement d'avis : « Untel a retiré son Amen » n'apprend rien de bon.
   */
  const reagir = async (cle) => {
    /* ⚠️ `!mienne`, PAS `mienne !== cle` — 20 octobre 2026, trouvé à l'audit.
       L'ancienne forme était vraie en CHANGEANT de réaction : Amen → J'adore
       → Wouah sonnait l'auteur à chaque hésitation, exactement ce que le
       commentaire ci-dessus promet d'éviter. Un ajout, c'est partir de rien. */
    const ajout = cle && !mienne;
    await Store.amens.poser(p.id, cle);
    if (!ajout) return;
    Store.notifs.add(p.authorId, {
      type: p.type === "priere" ? "priere" : "amen",
      // la réaction choisie voyage avec l'alerte : sa pastille en prend la
      // couleur chez le destinataire (24 octobre 2026)
      reaction: cle,
      deId: ctx.moi.uid,
      deNom: ctx.moi.nom,
      dePhoto: ctx.moi.photo || "",
      postId: p.id,
      apercu: (p.texte || "").slice(0, 80),
    });
  };

  /* L'appui COURT pose l'Amen, comme il l'a toujours fait ; l'appui LONG ouvre
     la palette. Sans ce garde-fou, le relâchement du doigt après un appui long
     poserait AUSSI un Amen — on ouvrirait la palette et l'on aurait déjà
     réagi avant d'avoir choisi. */
  const amen = () => {
    if (palette.absorbe()) return;
    reagir(mienne || "amen");
  };

  const type = TYPES[p.type];
  const vues = p.vues || 0;
  const partages = p.partages || 0;

  return (
    <article className="post" ref={carte} data-id={p.id} data-auteur={p.authorId}>
      {/* ⚠️ AU-DESSUS DE L'AUTEUR, comme sur Facebook. C'est ce qui fait
          s'arrêter sur une carte : on lit d'abord que quelqu'un qu'on connaît
          s'y est intéressé, l'auteur ensuite. Voir `EchoDuCercle.jsx`. */}
      <EchoDuCercle p={p} ctx={ctx} />
      <header className="post__tete">
        <a className="post__auteur" href={p.pageId ? `#/page/${p.pageId}` : `#/profil/${p.authorId}`}>
          {p.pageId ? (
            <span className="page-logo page-logo--petit">
              {p.pageImage ? <img src={p.pageImage} alt="" /> : "📄"}
            </span>
          ) : (
            <Avatar membre={auteur} taille={44} anneau={avecStatut.has(p.authorId)} />
          )}
          <span>
            <strong>
              {p.pageId ? p.pageNom || "Page" : auteur.nom}
              <Certifie membre={p.pageId ? { certifie: pageCertifiee } : auteur} taille={14} />
            </strong>
            <span className="post__meta">
              <span>{ilYA(p.createdAt)}</span>
              {/* ⚠️ UNE CORRECTION SE DIT — 24 août 2026, avec « Modifier ».
                  Réécrire en silence ce que dix personnes ont approuvé, ce
                  serait déplacer une signature sous les yeux de ceux qui ont
                  signé. Le téléphone porte la même mention. */}
              {p.modifieLe && <span className="petit texte-doux">modifié</span>}
              {p.type && p.type !== "post" && !GESTES[p.type] && (
                <span className={`puce puce--${p.type}`}>
                  {type?.icone || ""} {type?.label || p.type}
                </span>
              )}
            </span>
          </span>
        </a>
        <button className="icon-btn" aria-label="Options" onClick={() => menu(p, ctx, raisons)}>
          <Icone nom="options" taille={20} />
        </button>
      </header>

      {GESTES[p.type] && <div className="post__geste">{GESTES[p.type]}</div>}
      {p.texte && (
        <div className="post__texte">
          {/* ⚠️ QUI EST IDENTIFIÉ DANS CETTE PUBLICATION — 18 août 2026.
              `p.mentions` existait déjà : `TexteRiche` rend les « @Nom »
              cliquables DANS le texte. Mais rien ne le disait au-dessus, et
              l'on ne savait qu'on avait été cité qu'en lisant jusqu'au bout.

              ⚠️ « AVEC », ET NON « A ÉTÉ IDENTIFIÉE ». Le participe s'accorde,
              et `mentions` ne porte que `{ uid, nom }` : le genre n'y est pas.
              Écrire « identifiée » se tromperait une fois sur deux, et aller
              chercher chaque fiche pour l'accorder coûterait une lecture par
              nom sur chaque carte du fil. « Avec » ne s'accorde pas — et c'est
              ce que dit Facebook en français. */}
          {p.mentions?.length > 0 && (
            <p className="post__avec petit texte-doux">
              Avec{" "}
              {p.mentions.slice(0, 2).map((m, i) => (
                <span key={m.uid || i}>
                  {i > 0 ? " et " : ""}
                  <a className="post__avec-nom" href={`#/profil/${m.uid}`}>{m.nom}</a>
                </span>
              ))}
              {p.mentions.length > 2
                ? ` et ${p.mentions.length - 2} autre${p.mentions.length - 2 > 1 ? "s" : ""}`
                : ""}
            </p>
          )}
          <TexteRiche texte={p.texte} mentions={p.mentions} />
        </div>
      )}
      {p.partageDe && <Citation o={p.partageDe} ctx={ctx} />}
      {/* L'aperçu plein écran garde les gestes sous la photo (24 octobre
          2026) : l'Amen laisse l'image ouverte, Commenter y renvoie. */}
      <Media
        p={p}
        actions={[
          { icone: "amen", texte: libelleAmen(p.type, mienne), garder: true, surClic: amen },
          { icone: "commenter", texte: "Commenter", surClic: () => setCommentairesOuverts(true) },
          { icone: "partager", texte: "Partager", surClic: () => partager(p, ctx) },
        ]}
      />

      <div className="post__compteurs">
        <span>
          {/* Les pastilles empilées d'abord, le compte ensuite — c'est dans cet
              ordre qu'on lit une publication : ce qu'on en a pensé, puis
              combien étaient de cet avis. */}
          <Resume
            total={resume.total}
            tetes={resume.tetes}
            avecNombre={false}
            className="reaction-resume--post"
          />
          {/* ⚠️ LE COMPTE DES RÉACTIONS EST DEVENU UN BOUTON — 18 août 2026.
              On lisait « 12 amens » sans jamais savoir de qui. Demandé par
              Josaphat : « la liste des personnes qui ont réagi, comme sur
              Facebook ». Le compte des commentaires, lui, ne bouge pas : il
              mène déjà au bon endroit, c'est-à-dire à la publication. */}
          <button
            type="button"
            className="lien-nu"
            onClick={() => setQuiReagit(true)}
            disabled={!resume.total}
          >
            {compteurAmen(p, resume.total)}
          </button>
          {" · "}{pluriel(p.commentaires || 0, "commentaire")}
          {/* Le nombre de partages n'apparaît qu'à partir du premier : « 0
              partage » sous chaque publication ne dit rien et décourage. */}
          {partages > 0 && ` · ${pluriel(partages, "partage")}`}
        </span>
        <QuiAReagi
          ctx={ctx}
          postId={p.id}
          total={resume.total}
          ouvert={quiReagit}
          fermer={() => setQuiReagit(false)}
        />
        <span className="post__vues">
          {vues > 0 &&
            (p.authorId === ctx.moi.uid ? (
              // Le nombre de vues est visible de tous ; la LISTE, seulement de
              // l'auteur — c'est lui seul qui peut la demander au serveur.
              <>
                {"· "}
                <button className="post__vues-lien" onClick={() => montrerVues(p)}>
                  {pluriel(vues, "vue")}
                </button>
              </>
            ) : (
              `· ${pluriel(vues, "vue")}`
            ))}
        </span>
      </div>

      <div className="post__actions">
        <div className="post__action-enveloppe">
          {palette.ouverte && (
            <Palette
              mienne={mienne}
              onChoisir={(cle) => {
                palette.fermer();
                reagir(cle);
              }}
              onFermer={palette.fermer}
            />
          )}
          <button
            className={`post__action${soutenu ? " est-actif" : ""}${mienne && mienne !== "amen" ? " est-colore" : ""}`}
            onClick={amen}
            {...palette.appui.handlers}
          >
            {/* Tant qu'on n'a rien posé, l'icône Amen ordinaire — celle qui se
                remplit quand on la touche. Dès qu'on a choisi, c'est SA pastille
                qui prend la place, en couleur : on voit d'un coup d'œil ce qu'on
                a répondu, sans avoir à lire. */}
            {mienne && mienne !== "amen" ? (
              <Pastille cle={mienne} taille={21} titre="" />
            ) : (
              <IconeDuo nom="amen" taille={21} />
            )}
            <span>{libelleAmen(p.type, mienne)}</span>
          </button>
        </div>
        <button className="post__action" onClick={() => setCommentairesOuverts((v) => !v)}>
          <Icone nom="commenter" taille={21} />
          <span>Commenter</span>
        </button>
        <button className="post__action" onClick={() => partager(p, ctx)}>
          <Icone nom="partager" taille={21} />
          <span>Partager</span>
        </button>
      </div>

      {/* L'abonnement aux commentaires ne s'installe qu'ici, à l'ouverture :
          écouter d'avance les trente publications d'un fil coûterait trente
          lectures continues pour ce que personne ne regarde. */}
      {commentairesOuverts && <Commentaires post={p} ctx={ctx} />}
    </article>
  );
}
